import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, User, Eye, EyeOff, X, Calendar, Power, PowerOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { useNavigate } from "react-router-dom";
import { Header } from '../../components/layout/Header';

const MEMBER_TYPES = [
  { value: "medica", label: "Médica / Especialista", clinical: true },
  { value: "esteticista", label: "Esteticista", clinical: true },
  { value: "enfermagem", label: "Enfermagem", clinical: true },
  { value: "atendimento", label: "Atendimento / Recepção", clinical: false },
  { value: "outro", label: "Outro", clinical: false },
];

const EMPTY_PRO = {
  name: "", title: "", bio: "", photo_url: "", specialties: [], service_ids: [], active: true, member_type: "medica",
};
const EMPTY_ACCESS = { email: "", password: "", role: "professional", has_access: false };

function isClinical(type) {
  return MEMBER_TYPES.find((t) => t.value === type)?.clinical ?? true;
}

export default function TeamAdmin() {
  const [pros, setPros] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "new" | {pro?, user?}
  const [tab, setTab] = useState("profile"); // "profile" | "access"
  const [proForm, setProForm] = useState(EMPTY_PRO);
  const [accessForm, setAccessForm] = useState(EMPTY_ACCESS);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [p, u, s] = await Promise.all([
        api.get("/admin/professionals"),
        api.get("/admin/users"),
        api.get("/services"),
      ]);
      setPros(p.data);
      setUsers(u.data);
      setServices(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Build unified team list
  const team = [
    ...pros.map((p) => {
      const linkedUser = users.find((u) => u.professional_id === p.id);
      return { _type: "pro", pro: p, user: linkedUser };
    }),
    ...users
      .filter((u) => u.role === "admin" || !u.professional_id)
      .filter((u) => !pros.some((p) => users.find((lu) => lu.professional_id === p.id)?.id === u.id))
      .map((u) => ({ _type: "user_only", pro: null, user: u })),
  ];

  const openNew = () => {
    const clinical = isClinical(EMPTY_PRO.member_type);
    setProForm(EMPTY_PRO);
    setAccessForm({ ...EMPTY_ACCESS, has_access: clinical, role: "professional" });
    setTab("profile");
    setModal("new");
    setShowPwd(false);
  };

  const openEdit = (item) => {
    if (item.pro) {
      setProForm({
        name: item.pro.name || "",
        title: item.pro.title || "",
        bio: item.pro.bio || "",
        photo_url: item.pro.photo_url || "",
        specialties: item.pro.specialties || [],
        service_ids: item.pro.service_ids || [],
        active: item.pro.active ?? true,
        member_type: item.pro.member_type || "medica",
      });
    } else {
      setProForm({ ...EMPTY_PRO, name: item.user?.name || "", member_type: "atendimento" });
    }
    if (item.user) {
      setAccessForm({ email: item.user.email, password: "", role: item.user.role, has_access: true });
    } else {
      setAccessForm(EMPTY_ACCESS);
    }
    setTab(item.pro ? "profile" : "access");
    setModal(item);
    setShowPwd(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const editing = isNew ? null : modal;
      let proId = editing?.pro?.id;

      // Save professional profile if clinical OR has name (for all types with a name)
      if (proForm.name) {
        const proPayload = {
          name: proForm.name,
          title: proForm.title || proForm.name,
          bio: proForm.bio || " ",
          photo_url: proForm.photo_url || "",
          specialties: proForm.specialties,
          service_ids: proForm.service_ids,
          active: proForm.active,
          member_type: proForm.member_type,
        };
        if (proId) {
          await api.put(`/admin/professionals/${proId}`, proPayload);
        } else if (isClinical(proForm.member_type) || !editing?.user) {
          const { data } = await api.post("/admin/professionals", proPayload);
          proId = data.id;
        }
      }

      // Save user account if email provided
      const needsAccess = accessForm.has_access || isClinical(proForm.member_type);
      if (needsAccess && accessForm.email) {
        const userId = editing?.user?.id;
        const userPayload = {
          name: proForm.name || accessForm.email,
          email: accessForm.email,
          role: accessForm.role,
          professional_id: proId || null,
          ...(accessForm.password ? { password: accessForm.password } : {}),
        };
        if (userId) {
          await api.put(`/admin/users/${userId}`, userPayload);
        } else {
          if (!accessForm.password) { toast.error("Defina uma senha para o novo acesso"); setSaving(false); return; }
          await api.post("/admin/users", userPayload);
        }
      }

      toast.success(isNew ? "Membro adicionado" : "Salvo");
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (item) => {
    if (!window.confirm(`Excluir ${item.pro?.name || item.user?.name}?`)) return;
    try {
      if (item.user) await api.delete(`/admin/users/${item.user.id}`).catch(() => {});
      if (item.pro) await api.delete(`/admin/professionals/${item.pro.id}`).catch(() => {});
      toast.success("Excluído");
      load();
    } catch (err) { toast.error("Erro ao excluir"); }
  };

  const toggleActive = async (item) => {
    if (!item.pro) return;
    await api.put(`/admin/professionals/${item.pro.id}`, { ...item.pro, active: !item.pro.active });
    load();
  };

  const typeLabel = (t) => MEMBER_TYPES.find((m) => m.value === t)?.label || t || "—";
  const roleBadge = (r) => r === "admin"
    ? "bg-violet-900/40 text-violet-300 border border-violet-700/40"
    : "bg-emerald-900/40 text-emerald-300 border border-emerald-700/40";

  const isNew = modal === "new";
  const isEditing = !isNew && modal;

  return (
    <div>
      <Header title="Equipe" subtitle="Perfis públicos e acessos ao sistema" actions={
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors" style={{background:'#A0462D'}}>
          <Plus size={15} /> Novo membro
        </button>
      } />

      {loading ? <p className="admin-text-muted">Carregando...</p> : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-6 py-3 text-gray-500 font-semibold uppercase tracking-wider text-xs w-8" />
                <th className="text-left px-6 py-3 text-gray-500 font-semibold uppercase tracking-wider text-xs">Nome</th>
                <th className="text-left px-6 py-3 admin-text-muted font-medium uppercase tracking-wider text-xs hidden md:table-cell">Tipo</th>
                <th className="text-left px-6 py-3 admin-text-muted font-medium uppercase tracking-wider text-xs hidden lg:table-cell">Status</th>
                <th className="text-left px-6 py-3 text-gray-500 font-semibold uppercase tracking-wider text-xs">Acesso</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {team.map((item, i) => {
                const name = item.pro?.name || item.user?.name || item.user?.email;
                const photo = item.pro?.photo_url;
                const type = item.pro?.member_type;
                const active = item.pro ? item.pro.active : true;
                return (
                  <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                    <td className="pl-6 py-3">
                      {photo ? (
                        <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                          <User size={16} className="text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <p className="font-medium text-gray-900">{name}</p>
                      {item.pro?.title && <p className="text-xs text-gray-400">{item.pro.title}</p>}
                    </td>
                    <td className="px-6 py-3 hidden md:table-cell">
                      <span className="text-xs text-gray-500">{typeLabel(type)}</span>
                    </td>
                    <td className="px-6 py-3 hidden lg:table-cell">
                      {item.pro ? (
                        <span className={`text-xs px-2 py-0.5 rounded border ${active ? "bg-emerald-900/30 text-emerald-400 border-emerald-700/40" : "bg-zinc-800/40 text-zinc-500 border-zinc-700/40"}`}>
                          {active ? "Ativo" : "Inativo"}
                        </span>
                      ) : <span className="text-xs admin-text-muted opacity-40">—</span>}
                    </td>
                    <td className="px-6 py-3">
                      {item.user ? (
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${roleBadge(item.user.role)}`}>
                          {item.user.role === "admin" ? "Admin" : "Portal"}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Sem acesso</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {item.pro && (
                          <button onClick={() => nav(`/admin/professionals/${item.pro.id}/agenda`)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="Agenda">
                            <Calendar size={14} />
                          </button>
                        )}
                        <button onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="Editar">
                          <Pencil size={14} />
                        </button>
                        {item.pro && (
                          <button onClick={() => toggleActive(item)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title={active ? "Desativar" : "Ativar"}>
                            {active ? <PowerOff size={14} /> : <Power size={14} />}
                          </button>
                        )}
                        <button onClick={() => del(item)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors" title="Excluir">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200" style={{ maxHeight: "88vh" }}>

            {/* Header fixo */}
            <div className="flex items-center justify-between px-7 pt-6 pb-5 border-b border-gray-200 shrink-0 bg-gray-50 rounded-t-xl">
              <div className="flex items-center gap-4">
                {proForm.photo_url ? (
                  <img src={proForm.photo_url} alt="" className="w-11 h-11 rounded-full object-cover ring-2 ring-amber-500/30" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gray-200 flex items-center justify-center">
                    <User size={20} className="text-gray-400" />
                  </div>
                )}
                <div>
                  <h2 className="font-semibold text-base text-gray-900">
                    {isNew ? "Novo membro" : (modal?.pro?.name || modal?.user?.name || "Editar")}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {MEMBER_TYPES.find((t) => t.value === proForm.member_type)?.label}
                  </p>
                </div>
              </div>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors shrink-0"><X size={16} /></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-7 shrink-0 bg-white">
              {[{ key: "profile", label: "Perfil" }, { key: "access", label: "Acesso" }].map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors -mb-px ${tab === t.key ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Corpo scrollável */}
            <div className="overflow-y-auto flex-1 px-7 py-6 bg-white">
              {tab === "profile" && (
                <div className="space-y-6">
                  {/* Linha 1: foto + tipo + nome */}
                  <div className="flex gap-5 items-start">
                    <div className="shrink-0 w-24">
                      <label className="admin-label mb-2 block">Foto</label>
                      <ImageUploader value={proForm.photo_url} onChange={(url) => setProForm({ ...proForm, photo_url: url })}
                        compact />
                    </div>
                    <div className="flex-1 space-y-4">
                      <div>
                        <label className="admin-label">Tipo</label>
                        <select className="admin-input" value={proForm.member_type}
                          onChange={(e) => {
                            const t = e.target.value;
                            setProForm({ ...proForm, member_type: t });
                            if (isClinical(t) && !accessForm.has_access && !modal?.user) {
                              setAccessForm((a) => ({ ...a, has_access: true, role: "professional" }));
                            }
                          }}>
                          {MEMBER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="admin-label">Nome completo *</label>
                        <input className="admin-input" value={proForm.name}
                          onChange={(e) => setProForm({ ...proForm, name: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  {isClinical(proForm.member_type) && (
                    <>
                      <div>
                        <label className="admin-label">Título / Especialidade</label>
                        <input className="admin-input" placeholder="Ex: Biomédica Esteta" value={proForm.title}
                          onChange={(e) => setProForm({ ...proForm, title: e.target.value })} />
                      </div>
                      <div>
                        <label className="admin-label">Bio</label>
                        <textarea rows={3} className="admin-input resize-none" value={proForm.bio}
                          onChange={(e) => setProForm({ ...proForm, bio: e.target.value })} />
                      </div>
                      <div>
                        <label className="admin-label flex justify-between">
                          <span>Serviços que atende</span>
                          <span className="admin-text-muted normal-case font-normal">
                            {(proForm.service_ids || []).length}/{services.length} selecionados
                          </span>
                        </label>
                        <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 text-xs bg-gray-50">
                          {services.map((s) => {
                            const checked = (proForm.service_ids || []).includes(s.id);
                            return (
                              <label key={s.id} className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer transition-colors ${checked ? "bg-blue-50 border border-blue-200" : "border border-transparent hover:bg-white"}`}>
                                <input type="checkbox" className="accent-blue-600 shrink-0" checked={checked}
                                  onChange={() => setProForm((f) => ({
                                    ...f,
                                    service_ids: checked ? f.service_ids.filter((x) => x !== s.id) : [...(f.service_ids || []), s.id],
                                  }))} />
                                <span className="truncate text-gray-800">{s.name}</span>
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-xs admin-text-muted mt-1">Sem serviços vinculados, o profissional não aparece no agendamento.</p>
                      </div>
                      <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <label htmlFor="active-toggle" className="text-sm cursor-pointer text-gray-700">
                          Perfil ativo — visível no site e no agendamento
                        </label>
                        <input type="checkbox" id="active-toggle" className="accent-amber-400"
                          className="accent-blue-600" checked={proForm.active} onChange={(e) => setProForm({ ...proForm, active: e.target.checked })} />
                      </div>
                    </>
                  )}
                </div>
              )}

              {tab === "access" && (
                <div className="space-y-5">
                  {isClinical(proForm.member_type) ? (
                    <div className="flex items-center gap-3 p-4 border border-blue-200 rounded-lg bg-blue-50">
                      <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                      <p className="text-sm text-blue-700">Profissionais clínicos têm acesso ao portal de agendamentos.</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <label htmlFor="has-access" className="text-sm cursor-pointer text-gray-700">
                        Acesso ao painel
                      </label>
                      <input type="checkbox" id="has-access" className="accent-amber-400"
                        checked={accessForm.has_access}
                        onChange={(e) => setAccessForm({ ...accessForm, has_access: e.target.checked })} />
                    </div>
                  )}

                  {(accessForm.has_access || isClinical(proForm.member_type)) && (
                    <>
                      <div>
                        <label className="admin-label">E-mail de login</label>
                        <input type="email" className="admin-input" value={accessForm.email}
                          onChange={(e) => setAccessForm({ ...accessForm, email: e.target.value })} />
                      </div>
                      <div>
                        <label className="admin-label">{modal?.user ? "Nova senha" : "Senha *"}</label>
                        <div className="relative">
                          <input type={showPwd ? "text" : "password"} className="admin-input pr-10"
                            value={accessForm.password}
                            onChange={(e) => setAccessForm({ ...accessForm, password: e.target.value })}
                            placeholder={modal?.user ? "Deixe em branco para manter" : ""} />
                          <button type="button" onClick={() => setShowPwd((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 admin-text-muted hover:text-white">
                            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="admin-label">Nível de acesso</label>
                        <select className="admin-input" value={accessForm.role}
                          onChange={(e) => setAccessForm({ ...accessForm, role: e.target.value })}>
                          <option value="professional">Portal — vê apenas os próprios agendamentos</option>
                          <option value="admin">Admin — acesso total ao CRM</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer fixo */}
            <div className="flex justify-end gap-3 px-7 py-5 border-t border-gray-200 bg-gray-50 shrink-0 rounded-b-xl">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">Cancelar</button>
              <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50">
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
